import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState("checking"); // checking | valid | invalid
  const [firstName, setFirstName] = useState("");
  const [invalidMsg, setInvalidMsg] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); setInvalidMsg("Lien invalide."); return; }
    api.get(`/auth/reset/${token}`)
      .then(({ data }) => { setFirstName(data.first_name || ""); setState("valid"); })
      .catch((err) => { setState("invalid"); setInvalidMsg(formatApiError(err.response?.data?.detail)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Ton mot de passe doit contenir au moins 8 caractères."); return; }
    if (password !== confirm) { setError("Les deux mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      await api.post(`/auth/reset/${token}`, { password });
      await refresh();
      navigate("/espace");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-cream px-6 py-12 sm:px-8">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }} className="w-full max-w-[420px]">
        <div className="flex justify-center">
          <span className="logo-elevate text-6xl sm:text-7xl">elevate</span>
        </div>

        {state === "checking" && (
          <p data-testid="reset-checking" className="mt-12 text-center font-light text-ink-soft">Vérification de ton lien…</p>
        )}

        {state === "invalid" && (
          <div className="mt-12 text-center">
            <h1 className="font-sans text-2xl font-medium text-ink">Lien indisponible</h1>
            <p data-testid="reset-invalid" className="mt-3 rounded-lg bg-bordeaux-tint px-4 py-3 text-sm font-light text-bordeaux">
              {invalidMsg || "Ce lien n'est plus valide."}
            </p>
            <button data-testid="reset-forgot-again" onClick={() => navigate("/mot-de-passe-oublie")}
              className="mt-6 rounded-full bg-bordeaux px-6 py-3 text-sm font-medium text-white transition hover:bg-bordeaux-dark">
              Refaire une demande
            </button>
          </div>
        )}

        {state === "valid" && (
          <>
            <div className="mt-12 text-center">
              <h1 className="font-sans text-3xl font-medium text-ink sm:text-4xl">
                Nouveau mot de passe{firstName ? `, ${firstName}` : ""}
              </h1>
              <p className="mt-3 font-sans text-sm font-light text-ink-soft sm:text-base">
                Choisis ton nouveau mot de passe pour accéder à ton espace.
              </p>
            </div>
            <form onSubmit={submit} className="mt-10 space-y-5">
              <div>
                <label className="font-sans text-sm font-medium text-ink">Nouveau mot de passe</label>
                <input data-testid="reset-password-input" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required
                  className="mt-2 w-full rounded-xl border border-line bg-white px-5 py-4 font-sans text-ink outline-none transition placeholder:text-ink-muted focus:border-bordeaux focus:ring-2 focus:ring-bordeaux/10"
                  placeholder="Minimum 8 caractères" />
              </div>
              <div>
                <label className="font-sans text-sm font-medium text-ink">Confirme ton mot de passe</label>
                <input data-testid="reset-confirm-input" type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required
                  className="mt-2 w-full rounded-xl border border-line bg-white px-5 py-4 font-sans text-ink outline-none transition placeholder:text-ink-muted focus:border-bordeaux focus:ring-2 focus:ring-bordeaux/10"
                  placeholder="••••••••" />
              </div>
              {error && (
                <p data-testid="reset-error" className="rounded-lg bg-bordeaux-tint px-4 py-3 font-sans text-sm font-light text-bordeaux">{error}</p>
              )}
              <button data-testid="reset-submit-button" type="submit" disabled={loading}
                className="w-full rounded-full bg-bordeaux py-4 font-sans text-base font-medium text-white transition hover:bg-bordeaux-dark disabled:opacity-60">
                {loading ? "Enregistrement…" : "Enregistrer et me connecter"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
